import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

type Language = 'ps' | 'da';

interface Props {
  language: Language;
}

const PAGES = ['home', 'mission', 'projects', 'volunteer', 'donate', 'contact'] as const;
const SECTIONS: Record<string, string[]> = {
  home: ['hero', 'mission', 'impact', 'impact_stats', 'projects_preview', 'volunteer_preview', 'donate_preview', 'photo_slider', 'stories_preview', 'stats'],
  mission: ['hero', 'vision', 'values', 'cta'],
  projects: ['hero', 'list'],
  volunteer: ['hero', 'opportunities', 'testimonials'],
  donate: ['hero', 'tiers', 'how_to', 'cta'],
  contact: ['hero', 'form', 'info', 'multilingual'],
};

const LANG_INFO: Record<Language, { label: string; nativeLabel: string; flag: string }> = {
  ps: { label: 'Pashto', nativeLabel: 'پښتو', flag: '🇦🇫' },
  da: { label: 'Dari', nativeLabel: 'دری', flag: '🇦🇫' },
};

type TabType = 'pages' | 'projects' | 'volunteers' | 'stories';

interface CmsForm {
  title: string;
  subtitle: string;
  body: string;
  cta: string;
}

interface ProjectRow {
  id: string;
  title_en: string;
  description_en: string;
  title_ps: string;
  title_da: string;
  description_ps: string;
  description_da: string;
  image_url: string;
  category: string;
  status: string;
  location: string;
}

interface VolunteerRow {
  id: string;
  title_en: string;
  description_en: string;
  title_ps: string;
  title_da: string;
  description_ps: string;
  description_da: string;
  commitment: string;
  location: string;
  category: string;
  is_active: boolean;
}

interface StoryRow {
  id: string;
  slug: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  excerpt_en: string;
  excerpt_ps: string;
  excerpt_da: string;
  content_en: string;
  content_ps: string;
  content_da: string;
  author_en: string;
  author_ps: string;
  author_da: string;
  author_role_en: string;
  author_role_ps: string;
  author_role_da: string;
  image: string;
  category: string;
  read_time_en: string;
  read_time_ps: string;
  read_time_da: string;
  published: boolean;
}

export default function LanguageCmsEditor({ language }: Props) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('pages');
  const [activePage, setActivePage] = useState<string>('home');
  const [activeSection, setActiveSection] = useState<string>('hero');
  const [form, setForm] = useState<CmsForm>({ title: '', subtitle: '', body: '', cta: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cmsEntries, setCmsEntries] = useState<Record<string, CmsForm>>({});

  // English reference data fetched from DB
  const [englishRefs, setEnglishRefs] = useState<Record<string, CmsForm>>({});

  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectRow | null>(null);
  const [projectForm, setProjectForm] = useState({ title: '', description: '', image_url: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const [opportunities, setOpportunities] = useState<VolunteerRow[]>([]);
  const [selectedOpp, setSelectedOpp] = useState<VolunteerRow | null>(null);
  const [oppForm, setOppForm] = useState({ title: '', description: '', commitment: '', location: '', category: '' });

  const [stories, setStories] = useState<StoryRow[]>([]);
  const [selectedStory, setSelectedStory] = useState<StoryRow | null>(null);
  const [storyForm, setStoryForm] = useState({ title: '', excerpt: '', content: '', author: '', author_role: '', read_time: '', image: '' });

  const [pageLoading, setPageLoading] = useState(false);
  const [projLoading, setProjLoading] = useState(false);
  const [oppLoading, setOppLoading] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);

  // Stats state
  const [statItems, setStatItems] = useState<{ value: string; label: string }[]>([
    { value: '', label: '' },
    { value: '', label: '' },
    { value: '', label: '' },
    { value: '', label: '' },
  ]);
  const [statsVisible, setStatsVisible] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  const info = LANG_INFO[language];
  const isPs = language === 'ps';

  // ============ Fetch English Reference Data ============
  const fetchEnglishRefs = useCallback(async () => {
    const { data } = await supabase.from('cms_content').select('page, section, content').eq('language', 'en');
    const map: Record<string, CmsForm> = {};
    (data ?? []).forEach((row: any) => {
      map[`${row.page}__${row.section}`] = {
        title: row.content?.title ?? '',
        subtitle: row.content?.subtitle ?? '',
        body: row.content?.body ?? '',
        cta: row.content?.cta ?? '',
      };
    });
    setEnglishRefs(map);
  }, []);

  useEffect(() => { fetchEnglishRefs(); }, [fetchEnglishRefs]);

  // ============ Page Content ============
  const fetchCmsEntries = useCallback(async () => {
    setPageLoading(true);
    const { data } = await supabase.from('cms_content').select('page, section, content').eq('language', language);
    const map: Record<string, CmsForm> = {};
    (data ?? []).forEach((row: any) => {
      map[`${row.page}__${row.section}`] = {
        title: row.content?.title ?? '',
        subtitle: row.content?.subtitle ?? '',
        body: row.content?.body ?? '',
        cta: row.content?.cta ?? '',
      };
    });
    setCmsEntries(map);
    setPageLoading(false);
  }, [language]);

  useEffect(() => {
    if (activeTab === 'pages') fetchCmsEntries();
  }, [activeTab, fetchCmsEntries]);

  useEffect(() => {
    const key = `${activePage}__${activeSection}`;
    const saved = cmsEntries[key];
    setForm(saved ?? { title: '', subtitle: '', body: '', cta: '' });
  }, [activePage, activeSection, cmsEntries]);

  const handleSavePageContent = async () => {
    setSaving(true);
    const payload = {
      page: activePage,
      section: activeSection,
      language,
      content: form,
      updated_at: new Date().toISOString(),
    };
    await supabase.from('cms_content').upsert([payload], { onConflict: 'page,section,language' });
    setSaving(false);
    setSaved(true);
    fetchCmsEntries();
    setTimeout(() => setSaved(false), 2000);
  };

  // ============ Hero Stats ============
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    const { data } = await supabase
      .from('cms_content')
      .select('content')
      .eq('page', 'home')
      .eq('section', 'stats')
      .eq('language', language)
      .maybeSingle();

    if (data?.content?.stats && Array.isArray(data.content.stats)) {
      const arr = data.content.stats as { value: string; label: string }[];
      const filled = [...arr];
      while (filled.length < 4) filled.push({ value: '', label: '' });
      setStatItems(filled.slice(0, 4));
    } else {
      setStatItems([
        { value: '', label: '' },
        { value: '', label: '' },
        { value: '', label: '' },
        { value: '', label: '' },
      ]);
    }
    setStatsVisible(data?.content?.visible !== false);
    setStatsLoading(false);
  }, [language]);

  useEffect(() => {
    if (activeTab === 'pages' && activePage === 'home' && activeSection === 'stats') {
      fetchStats();
    }
  }, [activeTab, activePage, activeSection, fetchStats]);

  const handleSaveStats = async () => {
    setSaving(true);
    const nonEmpty = statItems.filter(s => s.value.trim() || s.label.trim());
    const payload = {
      page: 'home',
      section: 'stats',
      language,
      content: { stats: nonEmpty, visible: statsVisible },
      updated_at: new Date().toISOString(),
    };
    await supabase.from('cms_content').upsert([payload], { onConflict: 'page,section,language' });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // ============ Projects ============
  const fetchProjects = useCallback(async () => {
    setProjLoading(true);
    const { data } = await supabase
      .from('projects')
      .select('id, title_en, description_en, title_ps, title_da, description_ps, description_da, image_url, category, status, location')
      .order('created_at', { ascending: false });
    const rows = (data ?? []).map((r: any) => ({
      id: r.id,
      title_en: r.title_en ?? '',
      description_en: r.description_en ?? '',
      title_ps: r.title_ps ?? '',
      title_da: r.title_da ?? '',
      description_ps: r.description_ps ?? '',
      description_da: r.description_da ?? '',
      image_url: r.image_url ?? '',
      category: r.category ?? '',
      status: r.status ?? '',
      location: r.location ?? '',
    }));
    setProjects(rows);
    setProjLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'projects') fetchProjects();
  }, [activeTab, fetchProjects]);

  useEffect(() => {
    if (selectedProject) {
      setProjectForm({
        title: isPs ? selectedProject.title_ps : selectedProject.title_da,
        description: isPs ? selectedProject.description_ps : selectedProject.description_da,
        image_url: selectedProject.image_url ?? '',
      });
    }
  }, [selectedProject, isPs]);

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return;
    setUploading(true);
    setUploadProgress(0);
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
    const filePath = `projects/${fileName}`;
    const { error: uploadErr } = await supabase.storage
      .from('project-images')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });
    if (uploadErr) { setUploading(false); return; }
    const { data: urlData } = supabase.storage.from('project-images').getPublicUrl(filePath);
    if (urlData?.publicUrl) setProjectForm(prev => ({ ...prev, image_url: urlData.publicUrl }));
    setUploadProgress(100);
    setTimeout(() => { setUploading(false); setUploadProgress(0); }, 600);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleRemoveImage = () => {
    setProjectForm(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveProject = async () => {
    if (!selectedProject) return;
    setSaving(true);
    const updates = isPs
      ? { title_ps: projectForm.title, description_ps: projectForm.description }
      : { title_da: projectForm.title, description_da: projectForm.description };
    await supabase.from('projects').update({
      ...updates,
      image_url: projectForm.image_url,
      updated_at: new Date().toISOString(),
    }).eq('id', selectedProject.id);
    setSaving(false);
    setSaved(true);
    fetchProjects();
    setTimeout(() => setSaved(false), 2000);
  };

  // ============ Volunteer Opportunities ============
  const fetchOpportunities = useCallback(async () => {
    setOppLoading(true);
    const { data } = await supabase
      .from('volunteer_opportunities')
      .select('id, title_en, description_en, title_ps, title_da, description_ps, description_da, commitment, location, category, is_active')
      .order('created_at', { ascending: false });
    const rows = (data ?? []).map((r: any) => ({
      id: r.id,
      title_en: r.title_en ?? '',
      description_en: r.description_en ?? '',
      title_ps: r.title_ps ?? '',
      title_da: r.title_da ?? '',
      description_ps: r.description_ps ?? '',
      description_da: r.description_da ?? '',
      commitment: r.commitment ?? '',
      location: r.location ?? '',
      category: r.category ?? '',
      is_active: r.is_active ?? false,
    }));
    setOpportunities(rows);
    setOppLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'volunteers') fetchOpportunities();
  }, [activeTab, fetchOpportunities]);

  useEffect(() => {
    if (selectedOpp) {
      setOppForm({
        title: isPs ? selectedOpp.title_ps : selectedOpp.title_da,
        description: isPs ? selectedOpp.description_ps : selectedOpp.description_da,
        commitment: selectedOpp.commitment ?? '',
        location: selectedOpp.location ?? '',
        category: selectedOpp.category ?? '',
      });
    }
  }, [selectedOpp, isPs]);

  const handleSaveOpportunity = async () => {
    if (!selectedOpp) return;
    setSaving(true);
    const updates = isPs
      ? { title_ps: oppForm.title, description_ps: oppForm.description }
      : { title_da: oppForm.title, description_da: oppForm.description };
    await supabase.from('volunteer_opportunities').update({
      ...updates,
      commitment: oppForm.commitment,
      location: oppForm.location,
      category: oppForm.category,
      updated_at: new Date().toISOString(),
    }).eq('id', selectedOpp.id);
    setSaving(false);
    setSaved(true);
    fetchOpportunities();
    setTimeout(() => setSaved(false), 2000);
  };

  // ============ Stories ============
  const fetchStories = useCallback(async () => {
    setStoryLoading(true);
    const { data } = await supabase
      .from('stories')
      .select('*')
      .order('created_at', { ascending: false });
    setStories((data as StoryRow[]) ?? []);
    setStoryLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'stories') fetchStories();
  }, [activeTab, fetchStories]);

  useEffect(() => {
    if (selectedStory) {
      setStoryForm({
        title: isPs ? selectedStory.title_ps : selectedStory.title_da,
        excerpt: isPs ? selectedStory.excerpt_ps : selectedStory.excerpt_da,
        content: isPs ? selectedStory.content_ps : selectedStory.content_da,
        author: isPs ? selectedStory.author_ps : selectedStory.author_da,
        author_role: isPs ? selectedStory.author_role_ps : selectedStory.author_role_da,
        read_time: isPs ? selectedStory.read_time_ps : selectedStory.read_time_da,
        image: selectedStory.image ?? '',
      });
    }
  }, [selectedStory, isPs]);

  const handleSaveStory = async () => {
    if (!selectedStory) return;
    setSaving(true);
    const updates = isPs
      ? {
          title_ps: storyForm.title,
          excerpt_ps: storyForm.excerpt,
          content_ps: storyForm.content,
          author_ps: storyForm.author,
          author_role_ps: storyForm.author_role,
          read_time_ps: storyForm.read_time,
        }
      : {
          title_da: storyForm.title,
          excerpt_da: storyForm.excerpt,
          content_da: storyForm.content,
          author_da: storyForm.author,
          author_role_da: storyForm.author_role,
          read_time_da: storyForm.read_time,
        };
    await supabase.from('stories').update({
      ...updates,
      image: storyForm.image,
      updated_at: new Date().toISOString(),
    }).eq('id', selectedStory.id);
    setSaving(false);
    setSaved(true);
    fetchStories();
    setTimeout(() => setSaved(false), 2000);
  };

  const sections = SECTIONS[activePage] ?? [];
  const enRef = englishRefs[`${activePage}__${activeSection}`] ?? {};

  // ======== RENDER: Page Content Tab ========
  const renderPageContentTab = () => {
    if (activePage === 'home' && activeSection === 'stats') {
      return (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#3B9EF5]/10 text-[#3B9EF5]' : 'bg-amber-100 text-amber-700'}`}>
              {info.flag} {info.nativeLabel}
            </span>
            <span className="text-xs text-gray-400">— Hero Stats</span>
          </div>

          {statsLoading ? (
            <div className="py-12 text-center">
              <div className="w-6 h-6 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-bar-chart-grouped-line text-[#3B9EF5]"></i>
                <span className="text-sm font-semibold text-[#1B2838]">Hero Stats</span>
                <span className="text-xs text-gray-400">— 4 stat cards shown on the homepage hero</span>
              </div>
              <div className="flex items-center gap-3 mb-4 px-1">
                <button
                  onClick={() => setStatsVisible(v => !v)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${statsVisible ? 'bg-[#3B9EF5]' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${statsVisible ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
                <span className={`text-sm font-medium ${statsVisible ? 'text-[#1B2838]' : 'text-gray-400'}`}>
                  {statsVisible ? 'Stats visible on hero' : 'Stats hidden'}
                </span>
              </div>
              {statItems.map((stat, i) => (
                <div key={i} className="grid grid-cols-5 gap-3 items-end">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      {isPs ? 'شمیره' : 'مقدار'} #{i + 1}
                    </label>
                    <input
                      value={stat.value}
                      onChange={e => {
                        const next = [...statItems];
                        next[i] = { ...next[i], value: e.target.value };
                        setStatItems(next);
                      }}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] font-bold"
                      placeholder={isPs ? '۲,۵۰۰+' : '۲,۵۰۰+'}
                      dir="rtl"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      {isPs ? 'لیبل' : 'برچسب'}
                    </label>
                    <input
                      value={stat.label}
                      onChange={e => {
                        const next = [...statItems];
                        next[i] = { ...next[i], label: e.target.value };
                        setStatItems(next);
                      }}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder={isPs ? 'کورنۍ مرسته شوې' : 'خانواده‌های کمک شده'}
                      dir="rtl"
                    />
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-400">
                  {info.nativeLabel} / home / stats
                </span>
                <div className="flex items-center gap-3">
                  {saved && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line"></i> Saved!
                    </span>
                  )}
                  <button
                    onClick={handleSaveStats}
                    disabled={saving}
                    className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                  >
                    {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                    {isPs ? 'خوندي کول' : 'ذخیره'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* English Reference */}
      <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REFERENCE</span>
          <span className="text-xs text-gray-400">English (from database)</span>
        </div>
        {enRef.title && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.title}</div>
          </div>
        )}
        {enRef.subtitle && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Subtitle</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.subtitle}</div>
          </div>
        )}
        {enRef.body && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Body</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.body}</div>
          </div>
        )}
        {enRef.cta && (
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">CTA</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.cta}</div>
          </div>
        )}
        {!enRef.title && !enRef.subtitle && !enRef.body && !enRef.cta && (
          <div className="text-center py-10">
            <div className="text-sm text-amber-600 mb-1">No English content saved yet</div>
            <div className="text-xs text-gray-400">Save content in the English CMS first to see it here as reference.</div>
          </div>
        )}
      </div>

      {/* RTL Editor */}
      <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#3B9EF5]/10 text-[#3B9EF5]' : 'bg-amber-100 text-amber-700'}`}>
            {info.flag} {info.nativeLabel}
          </span>
        </div>

        {pageLoading ? (
          <div className="py-12 text-center">
            <div className="w-6 h-6 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
              <input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                placeholder={isPs ? 'پښتو کې عنوان ولیکئ...' : 'عنوان را به دری بنویسید...'}
                dir="rtl"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subtitle</label>
              <input
                value={form.subtitle}
                onChange={e => setForm({ ...form, subtitle: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                placeholder={isPs ? 'پښتو کې فرعي عنوان ولیکئ...' : 'زیرعنوان را به دری بنویسید...'}
                dir="rtl"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Body Content</label>
              <textarea
                value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })}
                rows={4}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] resize-none"
                placeholder={isPs ? 'پښتو کې متن ولیکئ...' : 'متن را به دری بنویسید...'}
                dir="rtl"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">CTA Button Text</label>
              <input
                value={form.cta}
                onChange={e => setForm({ ...form, cta: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                placeholder={isPs ? 'پښتو کې د تڼۍ متن ولیکئ...' : 'متن دکمه را به دری بنویسید...'}
                dir="rtl"
              />
            </div>
          </div>
        )}

        {activeSection === 'photo_slider' && (
          <div className="mt-4 px-4 py-3 bg-[#3B9EF5]/5 border border-[#3B9EF5]/20 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-image-line text-[#3B9EF5]"></i>
              <span className="text-sm text-gray-600">To add or manage actual photos, use the dedicated Photo Slider dashboard.</span>
            </div>
            <button
              onClick={() => navigate('/admin/photos')}
              className="px-4 py-2 bg-[#3B9EF5] text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-external-link-line"></i>
              Manage Photos
            </button>
          </div>
        )}

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {language === 'ps' ? 'پښتو' : 'دری'} / {activePage} / {activeSection}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i> Saved!
              </span>
            )}
            <button
              onClick={handleSavePageContent}
              disabled={saving}
              className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
            >
              {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
              Save Content
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  };

  // ======== RENDER: Projects Tab ========
  const renderProjectsTab = () => (
    <div className="flex gap-5">
      {/* Project List */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Projects</div>
        </div>
        <div className="max-h-[650px] overflow-y-auto">
          {projLoading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No projects found. Add projects first.</div>
          ) : (
            projects.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedProject(p)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${selectedProject?.id === p.id ? 'bg-[#3B9EF5]/5 border-l-2 border-l-[#3B9EF5]' : ''}`}
              >
                <div className="text-sm font-medium text-[#1B2838] line-clamp-2">{p.title_en}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">{p.category}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${p.status === 'active' ? 'bg-green-100 text-green-700' : p.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                </div>
                <div className="text-xs mt-1">
                  {isPs ? (
                    p.title_ps ? <span className="text-[#3B9EF5]" dir="rtl">{p.title_ps}</span> : <span className="text-amber-500 italic">Not translated</span>
                  ) : (
                    p.title_da ? <span className="text-[#3B9EF5]" dir="rtl">{p.title_da}</span> : <span className="text-amber-500 italic">Not translated</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6">
        {!selectedProject ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3"><i className="ri-file-text-line text-3xl"></i></div>
            <p className="text-sm">Select a project from the list to edit its {info.label} translation</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* English Reference */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REF</span>
                <span className="text-xs text-gray-400">English</span>
              </div>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
                <div className="text-sm text-gray-700 leading-relaxed font-medium">{selectedProject.title_en}</div>
              </div>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</div>
                <div className="text-sm text-gray-700 leading-relaxed">{selectedProject.description_en || 'No English description.'}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedProject.category && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{selectedProject.category}</span>}
                {selectedProject.status && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded capitalize">{selectedProject.status}</span>}
                {selectedProject.location && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{selectedProject.location}</span>}
              </div>
            </div>

            {/* RTL Editor */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#3B9EF5]/10 text-[#3B9EF5]' : 'bg-amber-100 text-amber-700'}`}>
                  {info.flag} {info.nativeLabel}
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
                  <input
                    value={projectForm.title}
                    onChange={e => setProjectForm({ ...projectForm, title: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                    placeholder={isPs ? 'پښتو کې د پروژې عنوان...' : 'عنوان پروژه به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                  <textarea
                    value={projectForm.description}
                    onChange={e => setProjectForm({ ...projectForm, description: e.target.value })}
                    rows={5}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] resize-none"
                    placeholder={isPs ? 'پښتو کې د پروژې تفصیل...' : 'توضیحات پروژه به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Project Image</label>

                  {/* Upload Zone */}
                  {!projectForm.image_url && (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        dragOver
                          ? 'border-[#3B9EF5] bg-[#3B9EF5]/5'
                          : 'border-gray-200 hover:border-[#3B9EF5]/50 hover:bg-gray-50'
                      }`}
                    >
                      {uploading ? (
                        <div className="space-y-3">
                          <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div className="bg-[#3B9EF5] h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress || 60}%` }}></div>
                          </div>
                          <p className="text-sm text-gray-500">Uploading image...</p>
                        </div>
                      ) : (
                        <>
                          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 rounded-full bg-[#3B9EF5]/10">
                            <i className="ri-image-add-line text-2xl text-[#3B9EF5]"></i>
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Drop your image here or <span className="text-[#3B9EF5] underline">browse</span>
                          </p>
                          <p className="text-xs text-gray-400">JPEG, PNG, WebP or GIF — max 5MB</p>
                        </>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}

                  {/* Image Preview */}
                  {projectForm.image_url && (
                    <div className="relative rounded-xl overflow-hidden bg-gray-100 group">
                      <img src={projectForm.image_url} alt="Project preview" className="w-full h-48 object-cover object-top" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2">
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-white text-gray-700 rounded-full text-xs font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap flex items-center gap-1">
                          <i className="ri-image-edit-line"></i> Replace
                        </button>
                        <button type="button" onClick={handleRemoveImage} className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-red-500 text-white rounded-full text-xs font-semibold cursor-pointer hover:bg-red-600 whitespace-nowrap flex items-center gap-1">
                          <i className="ri-delete-bin-line"></i> Remove
                        </button>
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFileSelect} className="hidden" />
                    </div>
                  )}

                  {/* URL Fallback */}
                  <div className="mt-2">
                    <label className="block text-xs text-gray-400 mb-1">Or paste an image URL instead:</label>
                    <input
                      value={projectForm.image_url}
                      onChange={e => setProjectForm({ ...projectForm, image_url: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> Saved!
                  </span>
                )}
                <button
                  onClick={handleSaveProject}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Translation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ======== RENDER: Volunteers Tab ========
  const renderVolunteersTab = () => (
    <div className="flex gap-5">
      {/* Opportunity List */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Opportunities</div>
        </div>
        <div className="max-h-[650px] overflow-y-auto">
          {oppLoading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : opportunities.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No opportunities found.</div>
          ) : (
            opportunities.map(o => (
              <button
                key={o.id}
                onClick={() => setSelectedOpp(o)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${selectedOpp?.id === o.id ? 'bg-[#3B9EF5]/5 border-l-2 border-l-[#3B9EF5]' : ''}`}
              >
                <div className="text-sm font-medium text-[#1B2838] line-clamp-2">{o.title_en}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">{o.category}</span>
                  {o.is_active ? <span className="text-xs text-green-600">Active</span> : <span className="text-xs text-gray-400">Inactive</span>}
                </div>
                <div className="text-xs mt-1">
                  {isPs ? (
                    o.title_ps ? <span className="text-[#3B9EF5]" dir="rtl">{o.title_ps}</span> : <span className="text-amber-500 italic">Not translated</span>
                  ) : (
                    o.title_da ? <span className="text-[#3B9EF5]" dir="rtl">{o.title_da}</span> : <span className="text-amber-500 italic">Not translated</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6">
        {!selectedOpp ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3"><i className="ri-team-line text-3xl"></i></div>
            <p className="text-sm">Select an opportunity from the list to edit its {info.label} translation</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* English Reference */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REF</span>
                <span className="text-xs text-gray-400">English</span>
              </div>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
                <div className="text-sm text-gray-700 leading-relaxed font-medium">{selectedOpp.title_en}</div>
              </div>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</div>
                <div className="text-sm text-gray-700 leading-relaxed">{selectedOpp.description_en || 'No English description.'}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedOpp.commitment && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{selectedOpp.commitment}</span>}
                {selectedOpp.category && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{selectedOpp.category}</span>}
                {selectedOpp.location && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{selectedOpp.location}</span>}
              </div>
            </div>

            {/* RTL Editor */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#3B9EF5]/10 text-[#3B9EF5]' : 'bg-amber-100 text-amber-700'}`}>
                  {info.flag} {info.nativeLabel}
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
                  <input
                    value={oppForm.title}
                    onChange={e => setOppForm({ ...oppForm, title: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                    placeholder={isPs ? 'پښتو کې د فرصت عنوان...' : 'عنوان فرصت به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                  <textarea
                    value={oppForm.description}
                    onChange={e => setOppForm({ ...oppForm, description: e.target.value })}
                    rows={5}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] resize-none"
                    placeholder={isPs ? 'پښتو کې د فرصت تفصیل...' : 'توضیحات فرصت به دری...'}
                    dir="rtl"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Commitment</label>
                    <input
                      value={oppForm.commitment}
                      onChange={e => setOppForm({ ...oppForm, commitment: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder="e.g. 5 hrs/week"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Location</label>
                    <input
                      value={oppForm.location}
                      onChange={e => setOppForm({ ...oppForm, location: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder="e.g. Remote"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                    <input
                      value={oppForm.category}
                      onChange={e => setOppForm({ ...oppForm, category: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder="e.g. Education"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> Saved!
                  </span>
                )}
                <button
                  onClick={handleSaveOpportunity}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Translation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ======== RENDER: Stories Tab ========
  const renderStoriesTab = () => (
    <div className="flex gap-5">
      {/* Story List */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stories</div>
          <button
            onClick={() => navigate('/admin/stories')}
            className="text-xs font-semibold text-[#3B9EF5] hover:text-[#2A8DE0] cursor-pointer flex items-center gap-1 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i> Create New
          </button>
        </div>
        <div className="max-h-[650px] overflow-y-auto">
          {storyLoading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : stories.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No stories found.
              <button
                onClick={() => navigate('/admin/stories')}
                className="block mx-auto mt-2 text-[#3B9EF5] font-semibold cursor-pointer hover:text-[#2A8DE0] whitespace-nowrap"
              >
                + Create your first story
              </button>
            </div>
          ) : (
            stories.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedStory(s)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${selectedStory?.id === s.id ? 'bg-[#3B9EF5]/5 border-l-2 border-l-[#3B9EF5]' : ''}`}
              >
                <div className="flex items-start gap-2">
                  {s.image && (
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                      <img src={s.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1B2838] line-clamp-2">{s.title_en}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{s.category}</span>
                      {s.published ? (
                        <span className="text-xs text-green-600">Published</span>
                      ) : (
                        <span className="text-xs text-amber-500">Draft</span>
                      )}
                    </div>
                    <div className="text-xs mt-0.5">
                      {isPs ? (
                        s.title_ps ? <span className="text-[#3B9EF5]" dir="rtl">{s.title_ps}</span> : <span className="text-amber-500 italic">Not translated</span>
                      ) : (
                        s.title_da ? <span className="text-[#3B9EF5]" dir="rtl">{s.title_da}</span> : <span className="text-amber-500 italic">Not translated</span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6">
        {!selectedStory ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3"><i className="ri-book-open-line text-3xl"></i></div>
            <p className="text-sm">Select a story from the list to edit its {info.label} translation</p>
            <p className="text-xs mt-1 text-gray-300">Stories are created in English via the Stories dashboard, then translated here</p>
          </div>
        ) : (
          <>
            {/* English Reference */}
            <div className="mb-6 pb-6 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REFERENCE</span>
                <span className="text-xs text-gray-400">English — {selectedStory.category}</span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
                  <div className="text-sm text-gray-700 leading-relaxed font-medium">{selectedStory.title_en}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Author</div>
                  <div className="text-sm text-gray-700">{selectedStory.author_en || '—'}{selectedStory.author_role_en ? `, ${selectedStory.author_role_en}` : ''}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Read Time</div>
                  <div className="text-sm text-gray-700">{selectedStory.read_time_en || '—'}</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Excerpt</div>
                <div className="text-sm text-gray-700 leading-relaxed">{selectedStory.excerpt_en || 'No English excerpt.'}</div>
              </div>
            </div>

            {/* RTL Editor */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#3B9EF5]/10 text-[#3B9EF5]' : 'bg-amber-100 text-amber-700'}`}>
                  {info.flag} {info.nativeLabel} Translation
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
                  <input
                    value={storyForm.title}
                    onChange={e => setStoryForm({ ...storyForm, title: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                    placeholder={isPs ? 'پښتو کې د کیسې عنوان...' : 'عنوان داستان به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Excerpt</label>
                  <textarea
                    value={storyForm.excerpt}
                    onChange={e => setStoryForm({ ...storyForm, excerpt: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] resize-none"
                    placeholder={isPs ? 'پښتو کې د کیسې لنډیز...' : 'خلاصه داستان به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Content (HTML)</label>
                  <textarea
                    value={storyForm.content}
                    onChange={e => setStoryForm({ ...storyForm, content: e.target.value })}
                    rows={8}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] resize-none font-mono"
                    placeholder={isPs ? 'پښتو کې د کیسې بشپړ متن...' : 'متن کامل داستان به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Cover Image URL</label>
                  <input
                    value={storyForm.image}
                    onChange={e => setStoryForm({ ...storyForm, image: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                    placeholder="https://..."
                  />
                  {storyForm.image && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-100 w-40 h-24">
                      <img src={storyForm.image} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Author Name</label>
                    <input
                      value={storyForm.author}
                      onChange={e => setStoryForm({ ...storyForm, author: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder={isPs ? 'لیکوال...' : 'نویسنده...'}
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Author Role</label>
                    <input
                      value={storyForm.author_role}
                      onChange={e => setStoryForm({ ...storyForm, author_role: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder={isPs ? 'د لیکوال رول...' : 'نقش نویسنده...'}
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Read Time</label>
                    <input
                      value={storyForm.read_time}
                      onChange={e => setStoryForm({ ...storyForm, read_time: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]"
                      placeholder={isPs ? 'د لوستلو وخت...' : 'زمان مطالعه...'}
                      dir="rtl"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> Saved!
                  </span>
                )}
                <button
                  onClick={handleSaveStory}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Translation
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[#1B2838]">{info.label} CMS</h1>
              <span className="text-lg">{info.flag}</span>
            </div>
            <p className="text-gray-500 text-sm mt-0.5">Translate and manage all {info.label} content — page sections, projects, volunteer opportunities, and stories. English reference is pulled from your English CMS.</p>
          </div>
        </div>

        {/* Page + Section Selectors (only for pages tab) */}
        {activeTab === 'pages' && (
          <div className="flex flex-wrap items-center gap-4 mb-5">
            <div className="bg-white rounded-xl border border-gray-100 p-1 flex gap-1">
              {PAGES.map(p => (
                <button
                  key={p}
                  onClick={() => { setActivePage(p); setActiveSection(SECTIONS[p]?.[0] ?? 'hero'); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${activePage === p ? 'bg-[#1B2838] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-1 flex gap-1">
              {sections.map(s => (
                <button
                  key={s}
                  onClick={() => setActiveSection(s)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${activeSection === s ? 'bg-[#3B9EF5] text-[#1B2838]' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {s.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-100 p-1.5 flex gap-1 w-fit mb-6">
          {([
            { key: 'pages' as TabType, label: 'Page Content', icon: 'ri-pages-line' },
            { key: 'projects' as TabType, label: 'Projects', icon: 'ri-folder-line' },
            { key: 'volunteers' as TabType, label: 'Volunteer Opportunities', icon: 'ri-team-line' },
            { key: 'stories' as TabType, label: 'Stories', icon: 'ri-book-open-line' },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${activeTab === tab.key ? 'bg-[#1B2838] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'pages' && renderPageContentTab()}
        {activeTab === 'projects' && renderProjectsTab()}
        {activeTab === 'volunteers' && renderVolunteersTab()}
        {activeTab === 'stories' && renderStoriesTab()}
      </main>
    </div>
  );
}